import { Box, Gap, P, SvgIcon, theme } from '@salecycle/ui-component-library';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useWorkflowForm } from '../../../../contexts/CampaignWorkflowFormContext';
import { useDisplayWebContent } from '../hooks/useDisplayWebContent';
import { useDrawer } from '../../../../state/Drawer/hooks/useDrawer';
import { WorkflowBox } from '../../../WorkflowBox/WorkflowBox';
import { WorkflowBoxHeader } from '../../../WorkflowBox/WorkflowBoxHeader';
import { WorkflowContent } from '../../../WorkflowBox/WorkflowContent';
import { WorkflowLabel } from '../../../WorkflowBox/WorkflowLabel';
import { DisplayWebContentThumbnail } from '../DisplayWebContentThumbnail';
import { WorkflowBoxFooter } from '../../../WorkflowBox/WorkflowBoxFooter';
import useOpenDrawerSettings from '../hooks/useOpenDrawerSettings';
import { useConvertMessageToSplitTest } from '../hooks/useConvertMessageToSplitTest';
import { useVariantSelector } from '../../../../state/SplitTests/selectors/useVariantSelector';
import { ControlGroupInfo } from './ControlGroupInfo';
import { useShouldShowMissingFieldsWarning } from './hooks/useShouldShowMissingFieldsWarning';
import { TriggersWorkflowLabel } from '../../Trigger/components/TriggersWorkflowLabel';
import { useSegmentSchedulingSelector } from '../state/selectors/useSegmentSchedulingSelector';
import { Canvas } from '../../../../../../domain/Canvas/Canvas';
import { useWorkFlowCanvas } from '../../../../contexts/CampaignFlowProvider';
import { AudienceSegmentsTooltip } from 'sections/onsite-campaigns/pages/CampaignEditor/tabs/CampaignWorkflowTab/components/Workflows/components/AudienceSegmentsTooltip';

const SelectedContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const SelectedContent = styled.div`
  display: flex;
`;

const Grow = styled.div`
  flex-grow: 1;
`;

export type DisplayWebContentProps = {
  id: string;
  isVariant?: boolean;
};

export function DisplayWebContent({ id, isVariant }: DisplayWebContentProps) {
  const { actions, getIsFocused, getIsTouched } = useWorkflowForm();
  const { t } = useTranslation('campaigns');
  const { drawer, closeDrawer } = useDrawer();
  const { openDrawerSettings } = useOpenDrawerSettings();
  const { getControlGroup, getControlGroupWeight } = useDisplayWebContent();
  const { state: workflowState } = useWorkFlowCanvas();

  const shouldShowMissingFieldsWarning = useShouldShowMissingFieldsWarning(id);
  const { removeFromFlow, getTemplate, getSegmentation, getName } = useDisplayWebContent();

  const name = getName(id);

  const template = useMemo(() => getTemplate(id), [getTemplate, id]);

  const segmentation = useMemo(() => getSegmentation(id), [getSegmentation, id]);

  const ControlGroup = useMemo(() => getControlGroup.controlGroupEnabled(id), [getControlGroup, id]);
  const ControlGroupWeight = useMemo(() => getControlGroupWeight(id), [getControlGroupWeight, id]);

  const removeWorkflow = useCallback(() => {
    closeDrawer();
    removeFromFlow(id);
    actions.setFocused(id, false);
    actions.setTouched(id, false);
  }, [actions, closeDrawer, id, removeFromFlow]);

  const hasSelectedContent = useMemo(() => !!(template?.id || template?.name), [template?.id, template?.name]);
  const hasSettingsSet = useMemo(() => hasSelectedContent || (!getIsFocused(id) && getIsTouched(id)), [
    hasSelectedContent,
    getIsFocused,
    id,
    getIsTouched,
  ]);

  const convertToSplitTest = useConvertMessageToSplitTest({ id });

  const variant = useVariantSelector(id);
  const scheduling = useSegmentSchedulingSelector(id);

  const onOpenConfiguration = useCallback(() => {
    openDrawerSettings(id, 'display-website-content');
  }, [id, openDrawerSettings]);

  const onOpenVariantConfiguration = useCallback(() => {
    openDrawerSettings(variant?.messageId, 'display-web-content-variant-settings');
  }, [openDrawerSettings, variant?.messageId]);

  const hasBranchNodes = Canvas.createFromState(workflowState).hasBranchNodes();

  return (
    <WorkflowBox
      variant={shouldShowMissingFieldsWarning ? 'Incomplete' : 'Default'}
      id={id}
      ariaLabel="Display web content workflow"
    >
      {isVariant ? (
        <>
          <WorkflowBoxHeader
            title={variant?.name + ': ' + variant?.weight + '%'}
            isActionMenuButtonOnly
            onOpenConfiguration={onOpenVariantConfiguration}
            actionsMenuAriaLabel={'Edit variant settings'}
            titleWithPercent
            variantId={variant?.messageId}
          />
        </>
      ) : (
        <WorkflowBoxHeader
          icon={<SvgIcon name="browser" size="32" color={theme.designToken.neutral700} />}
          title={t('onsite.editor.workflow.displayWebContentMessage.workflow.title')}
          name={name}
          nameAriaLabel={t('onsite.editor.workflow.displayWebContentMessage.workflow.nameArialable')}
          onOpenConfiguration={onOpenConfiguration}
          onRemove={removeWorkflow}
          onEnableSplitTest={convertToSplitTest}
          actionsMenuAriaLabel={'Edit Display web content settings'}
        />
      )}

      <TriggersWorkflowLabel id={variant?.messageId || id} level="message" />
      <WorkflowContent
        showValidationWarning={shouldShowMissingFieldsWarning}
        alertAriaLabel="Display web content incomplete settings"
      >
        {hasBranchNodes && (
          <>
            {scheduling.startDate && (
              <P style={{ padding: 0, margin: 0 }} variant="body2">
                <strong>Starts: </strong>
                {scheduling.startTime ? (
                  <>
                    {scheduling.startDate.toLocaleDateString()} <strong>at</strong> {scheduling.startTime} UTC
                  </>
                ) : (
                  scheduling.startDate.toLocaleDateString()
                )}{' '}
              </P>
            )}
            {scheduling.endDate && (
              <P style={{ padding: 0, margin: 0 }} variant="body2">
                <strong>Ends: </strong>
                {scheduling.endTime ? (
                  <>
                    {scheduling.endDate.toLocaleDateString()} <strong>at</strong> {scheduling.endTime} UTC
                  </>
                ) : (
                  scheduling.endDate.toLocaleDateString()
                )}{' '}
              </P>
            )}
          </>
        )}

        {hasSettingsSet ? (
          <SelectedContentContainer>
            <SelectedContent>
              <Grow>
                <Box stackItems={true}>
                  {hasSelectedContent && <WorkflowLabel label="Template:" text={template?.name} labelFullWidth />}
                  {!!segmentation?.length && (
                    <WorkflowLabel
                      labelFullWidth
                      label={`${t('onsite.editor.workflow.audience')}:`}
                      text={<AudienceSegmentsTooltip segmentation={segmentation} t={t} />}
                    />
                  )}
                </Box>
              </Grow>
              <Gap right={16} />
              {template?.id && <DisplayWebContentThumbnail id={id} />}
            </SelectedContent>

            {ControlGroup && !variant && (
              <ControlGroupInfo ControlGroupWeight={ControlGroupWeight} isSplitTest={false} />
            )}
          </SelectedContentContainer>
        ) : (
          <p>What content would you like to display?</p>
        )}
      </WorkflowContent>
      <WorkflowBoxFooter
        show={drawer.id === id && drawer.open}
        hasSettingsSet={hasSettingsSet}
        openConfigurationButtonLabel={
          isVariant
            ? t('onsite.editor.workflow.displayWebContentMessage.workflow.openConfigurationVariantButton')
            : t('onsite.editor.workflow.displayWebContentMessage.workflow.openConfigurationButton')
        }
        openConfigurationButtonAriaLabel={t(
          'onsite.editor.workflow.displayWebContentMessage.workflow.openConfigurationButtonAria',
        )}
        onOpenConfiguration={isVariant ? onOpenVariantConfiguration : onOpenConfiguration}
      />
    </WorkflowBox>
  );
}
